/**
 * podcastShow — structural helpers + the RUNTIME registry hook (CEL-1362).
 *
 * v2: episode + show CONTENT comes from GET /api/cellcog/podcast/episodes at
 * runtime (Mongo-backed, zero-deploy publishing — Nitish's ruling Aug 18).
 * The generated podcastEpisodes.js is RETIRED. This module keeps only:
 *   - SHOW_FALLBACK: the render-safe shape while the registry loads (and the
 *     SSR crawler HTML carries the real content regardless — see
 *     py/cellcog/api/podcast_pages.py).
 *   - usePodcastRegistry(): the fetch hook both pages share.
 *   - Pure helpers (sorting, related, duration/ISO formatting, ?t= parsing).
 *
 * Show-level CONTENT (subscribe links, tagline, intake email) is runtime and
 * edited via Rhea's publish_episode.py --show — never hand-edited here.
 */
import { useEffect, useState } from 'react';
import { getEpisodes } from '../../services/podcastApi';

/** Render-safe placeholder while the registry loads. Subscribe links come
 * from the runtime config only (empty here = no dead buttons, Rhea's rule). */
export const SHOW_FALLBACK = {
  name: 'Clocked In: An AI Employee Podcast',
  tagline: 'The show where AI employees interview each other about their jobs.',
  description: '',
  host: { name: 'Rhea', role: 'Head of Growth, CellCog', link: '/ai-organization' },
  artwork: '/assets/podcast/show/show-cover.jpg',
  feedUrl: 'https://feeds.transistor.fm/clocked-in-an-ai-employee-podcast',
  subscribe: {},
  // guestIntakeEmail removed Sep 10 2026 (CEL-2495): the host address never ships in
  // public config; /podcast/guests reveals it on click, the .md twin spells it in words.
};

/**
 * The registry hook: { episodes, show, loading }.
 * episodes are ALL entries the server returned (live for everyone; admins
 * also get staged ones for ?preview=1) — callers filter with the helpers.
 */
export const usePodcastRegistry = () => {
  const [state, setState] = useState({ episodes: [], show: SHOW_FALLBACK, loading: true });
  useEffect(() => {
    let cancelled = false;
    // Guest-preview token (CEL-1751): read straight from the URL so the
    // registry returns the matching staged episode to an anonymous guest —
    // without it the SPA re-fetch sees live-only and the episode page
    // client-redirects the guest to the hub.
    const previewToken = new URLSearchParams(window.location.search).get('token');
    getEpisodes(previewToken)
      .then((res) => {
        if (cancelled) return;
        setState({
          episodes: res.data.episodes || [],
          show: { ...SHOW_FALLBACK, ...(res.data.show || {}) },
          loading: false,
        });
      })
      .catch(() => {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      });
    return () => { cancelled = true; };
  }, []);
  return state;
};

// ---- Pure helpers (single source of truth for every consumer; D4) ----

/** Live episodes only — the ONLY list the public hub/schema may see. */
export const liveEpisodes = (episodes) => episodes.filter((e) => e.isLive);

/** Newest first for the hub list (reverse-chron entries, PRD §2.2). */
export const liveEpisodesNewestFirst = (episodes) =>
  [...liveEpisodes(episodes)].sort(
    (a, b) => (b.season - a.season) || (b.episodeNumber - a.episodeNumber),
  );

/** Look up one episode by slug. `allowStaged` = the admin preview lane
 * (?preview=1) — the server only sends staged entries to admins anyway. */
export const getEpisode = (episodes, slug, { allowStaged = false } = {}) => {
  const e = episodes.find((x) => x.slug === slug) || null;
  if (!e) return null;
  return (e.isLive || allowStaged) ? e : null;
};

/** Related episodes: explicit relatedSlugs first, then adjacent by number. */
export const relatedEpisodes = (episodes, episode, n = 3) => {
  const live = liveEpisodesNewestFirst(episodes).filter((e) => e.slug !== episode.slug);
  const explicit = (episode.relatedSlugs || [])
    .map((s) => live.find((e) => e.slug === s))
    .filter(Boolean);
  const rest = live.filter((e) => !explicit.includes(e));
  return [...explicit, ...rest].slice(0, n);
};

/** mm:ss chip from durationSeconds (h:mm:ss past the hour). */
export const formatDuration = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(Math.floor(s % 60)).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
};

/** ISO-8601 duration for PodcastEpisode schema (PT#M#S). */
export const isoDuration = (s) =>
  `PT${Math.floor(s / 60)}M${Math.floor(s % 60)}S`;

/** Parse a ?t= param: seconds ("95") or "1m35s" / "1h2m3s". NaN-safe → 0. */
export const parseTimeParam = (raw) => {
  if (!raw) return 0;
  if (/^\d+$/.test(raw)) return parseInt(raw, 10);
  const m = raw.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
  if (!m || (!m[1] && !m[2] && !m[3])) return 0;
  return (parseInt(m[1] || 0, 10) * 3600) + (parseInt(m[2] || 0, 10) * 60) + parseInt(m[3] || 0, 10);
};
